import React from 'react';
import { getSignGradient, getSignTextColor } from '@/features/shared/utils/colors/elementColors';
import { useOptionalLure } from '@/features/knowledge-center/components/LureContext';

interface ZodiacBadgeProps {
    signName: string;
    signNumber?: number | null;
    className?: string;
    /** Hide text on mobile/small screens */
    compact?: boolean;
    disableLureClick?: boolean;
    isRetrograde?: boolean;
}

export const ZodiacBadge: React.FC<ZodiacBadgeProps> = React.memo(({
    signName,
    signNumber,
    className = "",
    compact = false,
    disableLureClick = false,
    isRetrograde = false
}) => {
    const lureContext = useOptionalLure();
    const isClickable = !disableLureClick && !!lureContext && !!signName;

    if (!signName) return null;

    const gradient = signNumber ? getSignGradient(signNumber).replace('background: ', '') : '';
    const textColor = signNumber ? getSignTextColor(signNumber) : 'text-white';

    const ZODIAC_SYMBOLS: Record<number, string> = {
        1: '\u2648\uFE0E', 2: '\u2649\uFE0E', 3: '\u264A\uFE0E', 4: '\u264B\uFE0E',
        5: '\u264C\uFE0E', 6: '\u264D\uFE0E', 7: '\u264E\uFE0E', 8: '\u264F\uFE0E',
        9: '\u2650\uFE0E', 10: '\u2651\uFE0E', 11: '\u2652\uFE0E', 12: '\u2653\uFE0E'
    };

    const symbol = signNumber ? ZODIAC_SYMBOLS[signNumber] : '';

    const handleBadgeClick = (e: React.MouseEvent) => {
        if (!isClickable) return;
        e.stopPropagation();

        const NUMBER_TO_SIGN_KEY: Record<number, string> = {
            1: 'aries', 2: 'taurus', 3: 'gemini', 4: 'cancer', 5: 'leo', 6: 'virgo',
            7: 'libra', 8: 'scorpio', 9: 'sagittarius', 10: 'capricorn', 11: 'aquarius', 12: 'pisces'
        };

        const key = signNumber ? NUMBER_TO_SIGN_KEY[signNumber] : 'unknown';

        lureContext.openLure({
            id: `zodiac-${key}`,
            type: 'astrology',
            title: signName,
            value: `מזל ${signName}`,
            content: `זיקה סימבולית של מזל ${signName}.`
        }, e);
    };

    return (
        <div
            onClick={isClickable ? handleBadgeClick : undefined}
            className={`
                relative mx-auto w-fit rounded-full ${textColor} font-bold shadow-md ${className} 
                flex items-center justify-center gap-1.5 transition-all
                ${compact ? 'px-1.5 py-0.5 md:px-3 md:py-1' : 'px-3 py-1'}
                text-[11px] md:text-xs
                ${isClickable ? 'cursor-pointer hover:scale-105 active:scale-95 duration-200 hover:shadow-lg' : ''}
            `}
            style={{ background: gradient || 'rgba(255,255,255,0.1)' }}
        >
            <span className={compact ? 'hidden md:inline' : 'inline'}>{signName}</span>
            {symbol && <span className="opacity-90 text-sm md:text-base font-normal">{symbol}</span>}
            {isRetrograde && (
                <div 
                    className="absolute -top-1 -right-1 w-3.5 h-3.5 sm:w-4 sm:h-4 bg-amber-500 text-mystic-950 text-[8px] sm:text-[9px] font-black rounded-full flex items-center justify-center border border-mystic-950 shadow-lg animate-pulse"
                    title="בנסיגה"
                >
                    R
                </div>
            )}
        </div>
    );
});

